import { useState, useEffect } from 'react';
import { X, Umbrella } from 'lucide-react';
import { Button } from '../../../components/Shared/Button';
import { TextArea } from '../../../components/Shared/TextArea';
import { useAbsenceTypeStore } from '../../../store/useAbsenceTypeStore';

interface AbsenceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  date: string;
  employeeName: string;
  onSave: (absenceTypeId: string, notes?: string) => void;
}

export function AbsenceDialog({ isOpen, onClose, date, employeeName, onSave }: AbsenceDialogProps) {
  const { absenceTypes, fetchAbsenceTypes } = useAbsenceTypeStore();
  const [selectedType, setSelectedType] = useState('');
  const [notes, setNotes] = useState('');

  // Load absence types on mount
  useEffect(() => {
    fetchAbsenceTypes();
  }, [fetchAbsenceTypes]);

  useEffect(() => {
    if (isOpen) {
      setSelectedType('');
      setNotes('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (!selectedType) return;
    onSave(selectedType, notes || undefined);
    onClose();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { 
      weekday: 'long', 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Umbrella className="w-5 h-5" />
              Abwesenheit eintragen
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {employeeName} · {formatDate(date)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Schließen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Absence Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Abwesenheitsart
            </label>
            <div className="space-y-2">
              {absenceTypes.map((type) => (
                <label
                  key={type.absenceTypeId}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                    ${selectedType === type.absenceTypeId 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="absenceType"
                    value={type.absenceTypeId}
                    checked={selectedType === type.absenceTypeId}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  {/* Dynamic color from database - inline style required */}
                  <span 
                    className="px-2 py-1 rounded text-sm font-medium"
                    style={{ 
                      backgroundColor: type.color ? `${type.color}20` : '#e5e7eb',
                      color: type.color || '#374151'
                    }}
                  >
                    {type.name}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <TextArea
              label="Bemerkung (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Zusätzliche Informationen..."
              rows={3}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={!selectedType}>
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}

